import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  category: 'essential' | 'business' | 'growth';
  optional?: boolean;
  /** Acción global en vez de navegación (ej. abrir el modal de alta rápida) */
  actionId?: 'quick-add-service';
}

interface OnboardingGuideState {
  currentStep: OnboardingStep | null;
  nextStep: OnboardingStep | null;
  completedSteps: string[];
  showGuidance: boolean;
  progress: number;
  essentialProgress: { completed: number; total: number };
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'complete-profile',
    title: 'Completa tu perfil',
    description: 'Añade tu información de facturación y datos fiscales',
    path: '/profile?edit=true',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    category: 'essential'
  },
  {
    id: 'setup-rates',
    title: 'Configura tus tarifas',
    description: 'Define los precios de tus servicios turísticos',
    path: '/facturacion/tarifas',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
      </svg>
    ),
    category: 'essential'
  },
  {
    id: 'create-invoice-template',
    title: 'Personaliza tu factura',
    description: 'Crea tu primera plantilla de facturación',
    path: '/facturacion/templates',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    category: 'essential'
  },
  {
    id: 'add-first-client',
    title: 'Añade tu primer cliente',
    description: 'Registra tu primera agencia o cliente directo',
    path: '/clientes/nuevo',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    category: 'business'
  },
  {
    id: 'create-first-service',
    title: 'Crea tu primer servicio',
    description: 'Registra tu primera actividad turística',
    path: '/servicios',
    actionId: 'quick-add-service',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    category: 'business'
  },
  {
    id: 'install-pwa',
    title: 'Instala la app',
    description: 'Añade GuideMe a tu escritorio como una aplicación',
    path: '#',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    category: 'growth',
    optional: true
  },
  {
    id: 'invite-colleagues',
    title: 'Invita a un amigo',
    description: 'Comparte GuideMe con otros guías',
    path: '/referidos',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
      </svg>
    ),
    category: 'growth',
    optional: true
  }
];

export const useOnboardingGuide = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [state, setState] = useState<OnboardingGuideState>({
    currentStep: null,
    nextStep: null,
    completedSteps: [],
    showGuidance: true,
    progress: 0,
    essentialProgress: { completed: 0, total: 3 }
  });

  const [showStepCompleteToast, setShowStepCompleteToast] = useState(false);
  const [recentlyCompletedStep, setRecentlyCompletedStep] = useState<OnboardingStep | null>(null);
  const [forceRenderKey, setForceRenderKey] = useState(0);

  // Load onboarding state from localStorage and backend
  const loadOnboardingState = useCallback(async () => {
    if (!user?.id || !token) return;

    try {
      // Load from localStorage first for immediate UI update
      const saved = localStorage.getItem(`onboarding_guidance_${user.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        setState(prev => ({
          ...prev,
          completedSteps: parsed.completedSteps || [],
          showGuidance: parsed.showGuidance ?? true
        }));
      }

      // Then sync with backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const completedSteps = data.completedSteps || [];
        
        // Use a Promise that resolves after state is updated
        return new Promise<void>((resolve) => {
          setState(prev => {
            const newState = {
              ...prev,
              completedSteps,
              showGuidance: data.showOnboarding ?? true
            };
            
            // Calculate progress
            const totalSteps = ONBOARDING_STEPS.length;
            const progress = Math.round((completedSteps.length / totalSteps) * 100);
            
            // Calculate essential progress
            const essentialSteps = ONBOARDING_STEPS.filter(step => step.category === 'essential');
            const completedEssential = essentialSteps.filter(step => completedSteps.includes(step.id)).length;
            
            // Find current and next step
            const nextIncompleteStep = ONBOARDING_STEPS.find(step => !completedSteps.includes(step.id));
            
            const finalState = {
              ...newState,
              progress,
              essentialProgress: {
                completed: completedEssential,
                total: essentialSteps.length
              },
              currentStep: nextIncompleteStep || null,
              nextStep: nextIncompleteStep || null
            };

            // Force re-render and resolve after state update
            setTimeout(() => {
              setForceRenderKey(prev => prev + 1);
              resolve();
            }, 0);
            
            return finalState;
          });

          // Save updated state to localStorage
          localStorage.setItem(`onboarding_guidance_${user.id}`, JSON.stringify({
            completedSteps,
            showGuidance: data.showOnboarding ?? true
          }));
        });
      }
    } catch (error) {
      console.warn('Could not load onboarding state:', error);
    }
  }, [user?.id, token]);

  // Mark step as completed
  const completeStep = useCallback(async (stepId: string) => {
    if (!user?.id || !token) return;
    
    const step = ONBOARDING_STEPS.find(s => s.id === stepId);
    if (!step) return;

    // Update local state immediately and return a Promise that resolves when state is updated
    return new Promise<void>((resolve) => {
      setState(prev => {
        // Add step if not already completed, or force update if already there
        const newCompletedSteps = prev.completedSteps.includes(stepId) 
          ? prev.completedSteps 
          : [...prev.completedSteps, stepId];
        const totalSteps = ONBOARDING_STEPS.length;
        const progress = Math.round((newCompletedSteps.length / totalSteps) * 100);
        
        const essentialSteps = ONBOARDING_STEPS.filter(step => step.category === 'essential');
        const completedEssential = essentialSteps.filter(step => newCompletedSteps.includes(step.id)).length;
        
        const nextIncompleteStep = ONBOARDING_STEPS.find(step => !newCompletedSteps.includes(step.id));
        
        const newState = {
          ...prev,
          completedSteps: newCompletedSteps,
          progress,
          essentialProgress: {
            completed: completedEssential,
            total: essentialSteps.length
          },
          currentStep: nextIncompleteStep || null,
          nextStep: nextIncompleteStep || null
        };

        // Check if onboarding is now 100% complete
        if (progress === 100 && prev.progress !== 100) {
          // Call completion endpoint asynchronously
          setTimeout(() => {
            sendOnboardingCompletionNotification();
          }, 500); // Small delay to ensure state update is processed
        }

        // Force re-render and resolve after state update
        setTimeout(() => {
          setForceRenderKey(prev => prev + 1);
          resolve();
        }, 0);

        return newState;
      });

      // Show completion toast
      setRecentlyCompletedStep(step);
      setShowStepCompleteToast(true);

      // Save to localStorage
      const saved = localStorage.getItem(`onboarding_guidance_${user.id}`);
      const currentData = saved ? JSON.parse(saved) : {};
      const newCompletedSteps = [...(currentData.completedSteps || []), stepId];
      localStorage.setItem(`onboarding_guidance_${user.id}`, JSON.stringify({
        ...currentData,
        completedSteps: newCompletedSteps
      }));

      // Sync with backend
      (async () => {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/complete-onboarding-step`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ stepId })
          });
          
          if (!response.ok) {
            const responseText = await response.text();
            throw new Error(`HTTP ${response.status}: ${responseText}`);
          }
        } catch (error) {
          console.warn('Could not sync step completion to backend:', error);
        }
      })();
    });
  }, [user?.id, token]);

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    if (state.nextStep) {
      navigate(state.nextStep.path);
    }
  }, [state.nextStep, navigate]);

  // Toggle guidance visibility
  const toggleGuidance = useCallback(async (show: boolean) => {
    if (!user?.id) return;

    setState(prev => ({ ...prev, showGuidance: show }));

    // Save to localStorage
    const saved = localStorage.getItem(`onboarding_guidance_${user.id}`);
    const currentData = saved ? JSON.parse(saved) : {};
    localStorage.setItem(`onboarding_guidance_${user.id}`, JSON.stringify({
      ...currentData,
      showGuidance: show
    }));

    // Sync with backend
    if (token) {
      try {
        const endpoint = show ? 'show-onboarding' : 'dismiss-onboarding';
        await fetch(`${import.meta.env.VITE_API_URL}/api/user/${endpoint}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.warn('Could not sync guidance state to backend:', error);
      }
    }
  }, [user?.id, token]);

  const dismissStepCompleteToast = useCallback(() => {
    setShowStepCompleteToast(false);
    setRecentlyCompletedStep(null);
  }, []);

  // Send completion notification when onboarding reaches 100%
  const sendOnboardingCompletionNotification = useCallback(async () => {
    if (!user?.id || !token) return;

    // Check localStorage to prevent duplicate calls
    const notificationSentKey = `onboarding_completion_notified_${user.id}`;
    const alreadyNotified = localStorage.getItem(notificationSentKey);
    
    if (alreadyNotified) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Mark as notified to prevent duplicates
        localStorage.setItem(notificationSentKey, 'true');
      } else {
        const errorData = await response.json();
        console.warn('Failed to send onboarding completion notification:', errorData);
      }
    } catch (error) {
      console.warn('Error sending onboarding completion notification:', error);
    }
  }, [user?.id, token]);

  // Initialize state on mount
  useEffect(() => {
    loadOnboardingState();
  }, [loadOnboardingState]);

  return {
    ...state,
    completeStep,
    goToNextStep,
    toggleGuidance,
    refreshState: loadOnboardingState,
    showStepCompleteToast,
    recentlyCompletedStep,
    dismissStepCompleteToast,
    isComplete: state.progress === 100,
    hasNextStep: !!state.nextStep,
    forceRenderKey // Export the key for components to force re-render
  };
};